import { useCallback, useMemo, useState } from "react"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useKeywordAnalytics } from "@/api/hooks/offers"
import { formatCurrency, formatNumber } from "../lib/formatters"

/**
 * Keywords analytics modal. Fetches `/api/analytics/keywords` and renders
 * Keyword, GClicks, RPC, Revenue (same columns as Ads Manager keywords modal).
 */

type SortField = "keyword" | "revenue" | "gclicks" | "rpc"

interface KeywordsModalProps {
  open: boolean
  onClose: () => void
  offerId: number | null
  offerName: string
  startDate: string
  endDate: string
}

export function KeywordsModal({
  open,
  onClose,
  offerId,
  offerName,
  startDate,
  endDate,
}: KeywordsModalProps) {
  const [sortField, setSortField] = useState<SortField>("revenue")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  const { data, isLoading, isError, error } = useKeywordAnalytics(
    {
      offer_id: offerId ?? undefined,
      start_date: startDate,
      end_date: endDate,
    },
    { enabled: open && offerId != null },
  )

  const keywords = useMemo(() => data?.data ?? [], [data?.data])
  const debugInfo = data?.debug

  const sorted = useMemo(() => {
    const arr = [...keywords]
    const mult = sortDir === "asc" ? 1 : -1
    arr.sort((a, b) => {
      if (sortField === "keyword") {
        return mult * (a.keyword || "").localeCompare(b.keyword || "")
      }
      const va = (a as unknown as Record<string, unknown>)[sortField] as number | null
      const vb = (b as unknown as Record<string, unknown>)[sortField] as number | null
      const na = va ?? (sortDir === "desc" ? -Infinity : Infinity)
      const nb = vb ?? (sortDir === "desc" ? -Infinity : Infinity)
      return mult * ((na as number) - (nb as number))
    })
    return arr
  }, [keywords, sortField, sortDir])

  const totals = useMemo(() => {
    let totalRevenue = 0
    let totalGclicks = 0

    for (const k of keywords) {
      totalRevenue += k.revenue ?? 0
      totalGclicks += k.gclicks ?? 0
    }

    const rpc =
      totalGclicks > 0 ? totalRevenue / totalGclicks : null

    return {
      revenue: totalRevenue,
      gclicks: totalGclicks,
      rpc,
    }
  }, [keywords])

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
      } else {
        setSortField(field)
        setSortDir(field === "keyword" ? "asc" : "desc")
      }
    },
    [sortField],
  )

  const SortIndicator = ({ field }: { field: SortField }) =>
    sortField === field ? (
      <span className="text-[0.7rem] text-[#8b9aff]" aria-hidden>
        {sortDir === "asc" ? "\u2191" : "\u2193"}
      </span>
    ) : null

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto border-[#4a4a4a] bg-[#2a2a2a] text-[#e0e0e0]">
        <DialogHeader>
          <DialogTitle>Keywords - {offerName}</DialogTitle>
        </DialogHeader>

        <div className="overflow-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                {(
                  [
                    { field: "keyword" as SortField, label: "Keyword", align: "left" as const },
                    { field: "gclicks" as SortField, label: "GClicks", align: "right" as const },
                    { field: "rpc" as SortField, label: "RPC", align: "right" as const },
                    { field: "revenue" as SortField, label: "Revenue", align: "right" as const },
                  ] as const
                ).map(({ field, label, align }) => (
                  <th
                    key={field}
                    className={`cursor-pointer select-none whitespace-nowrap border-b border-[#4a4a4a] px-3 py-2 text-xs font-semibold uppercase text-[#b0b0b0] ${
                      align === "right" ? "text-right" : "text-left"
                    }`}
                    onClick={() => handleSort(field)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}
                      <SortIndicator field={field} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-[#b0b0b0]">
                    Loading keywords...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td
                    colSpan={4}
                    className="p-4 text-center"
                    style={{ color: "#ff6b6b" }}
                  >
                    Error loading keywords:{" "}
                    {error instanceof Error ? error.message : "Unknown error"}
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-[#b0b0b0]">
                    <div>No keyword data found</div>
                    {debugInfo && (
                      <div className="mt-1 text-xs text-[#888]">
                        Debug: {JSON.stringify(debugInfo)}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-[#888]">
                      {"\uD83D\uDCA1"} Tip: Make sure you ran a recent data sync for this date range.
                    </div>
                  </td>
                </tr>
              ) : (
                sorted.map((kw, idx) => (
                  <tr key={idx} className="hover:bg-[#333]">
                    <td className="border-b border-[#3a3a3a] px-3 py-2 text-[#e0e0e0]">
                      {kw.keyword}
                    </td>
                    <td className="border-b border-[#3a3a3a] px-3 py-2 text-right text-[#e0e0e0]">
                      {formatNumber(kw.gclicks)}
                    </td>
                    <td className="border-b border-[#3a3a3a] px-3 py-2 text-right text-[#e0e0e0]">
                      {formatCurrency(kw.rpc)}
                    </td>
                    <td className="border-b border-[#3a3a3a] px-3 py-2 text-right text-[#e0e0e0]">
                      {formatCurrency(kw.revenue)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {/* Totals footer */}
            {sorted.length > 0 && (
              <tfoot>
                <tr
                  style={{
                    backgroundColor: "#4a4a4a",
                    borderTop: "2px solid #8b9aff",
                  }}
                >
                  <td className="px-3 py-2 font-semibold text-[#e0e0e0]">
                    Total
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-[#e0e0e0]">
                    {formatNumber(totals.gclicks)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-[#e0e0e0]">
                    {formatCurrency(totals.rpc)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-[#e0e0e0]">
                    {formatCurrency(totals.revenue)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </DialogContent>
    </Dialog>
  )
}
